import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import Switch from 'react-switch';
import Swal from 'sweetalert2';
import { selectAdminToken } from '../../sessionSlice';
import { fetchThemeData } from '../../themeSlice';
import { updateThemeData } from '../../functions/updateThemeData';
import { apiUrl } from '../../../utils/apiUrl';
import {
  getActiveGame,
  getGameDisplayName,
  GAME_CATALOG,
  resolveGameIconUrl,
  badgeImageUrl,
  dbTypeToActiveGame,
} from '../../../utils/gameHelpers';
import '../game-selector/gameSelector.css';
import './gameUploads.css';

const FILTER_ALL = 'all';

const GAME_TYPE_LABEL = {
  cheers_badge: 'Cheers to Peers',
  badges_upload: 'Badges of Trust',
  mens_upload: "Men's Day",
  boss_nomination: 'Every Boss Counts',
  freedom_story: 'My Freedom',
};

function matchesSearch(row, query) {
  const q = query.trim().toLowerCase();
  if (!q) return true;

  const haystack = [
    row.name,
    row.category,
    row.message,
    row.approval || 'approved',
    GAME_TYPE_LABEL[row.gameType] || row.gameType,
    row.colleagueName,
    row.colleagueEmail,
    row.email,
  ]
    .map((v) => String(v || '').toLowerCase())
    .join(' ');

  return haystack.includes(q);
}

export default function GameUploads() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const adminToken = useSelector(selectAdminToken);
  const { data: themeData, currentTheme } = useSelector((s) => s.theme);
  const activeGame = getActiveGame(themeData);
  const autoApprove = String(themeData?.upload_approval_required ?? '').toLowerCase() !== 'true';
  const [savingApproval, setSavingApproval] = useState(false);

  const [gameFilter, setGameFilter] = useState(activeGame || FILTER_ALL);
  const [search, setSearch] = useState('');
  const [uploads, setUploads] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (activeGame) setGameFilter(activeGame);
  }, [activeGame]);

  const loadUploads = useCallback(async () => {
    if (!adminToken) return;
    setLoading(true);
    try {
      const resp = await fetch(apiUrl('/adminGameUploads'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${adminToken}`,
        },
        body: JSON.stringify({
          activeGame: gameFilter === FILTER_ALL ? FILTER_ALL : gameFilter,
          gameFilter,
        }),
      });
      const json = await resp.json();
      setUploads(json.success && Array.isArray(json.uploads) ? json.uploads : []);
    } finally {
      setLoading(false);
    }
  }, [adminToken, gameFilter]);

  useEffect(() => {
    loadUploads();
  }, [loadUploads]);

  const filterLabel = useMemo(() => {
    if (gameFilter === FILTER_ALL) return 'All games';
    return getGameDisplayName(gameFilter);
  }, [gameFilter]);

  const canBulkAct = gameFilter !== FILTER_ALL;

  const filteredUploads = useMemo(
    () => uploads.filter((row) => matchesSearch(row, search)),
    [uploads, search]
  );

  useEffect(() => {
    setSearch('');
  }, [gameFilter]);

  const uploadScopeGame = (upload) => dbTypeToActiveGame(upload?.gameType) || gameFilter || activeGame;

  const persistAutoApprove = async (checked) => {
    if (!adminToken || !currentTheme) {
      Swal.fire('No theme', 'Select a theme before changing Auto Approve.', 'warning');
      return;
    }
    setSavingApproval(true);
    try {
      await updateThemeData({
        payload: {
          currentTheme,
          data: {
            active_game: activeGame,
            upload_approval_required: checked ? 'false' : 'true',
          },
        },
        token: adminToken,
      });
      await dispatch(fetchThemeData({ themeId: currentTheme, isAdmin: true })).unwrap();
    } catch (err) {
      Swal.fire('Error', err.message || 'Could not save Auto Approve.', 'error');
      await dispatch(fetchThemeData({ themeId: currentTheme, isAdmin: true }));
    } finally {
      setSavingApproval(false);
    }
  };

  const setApproval = async (upload, approval) => {
    const scopeGame = uploadScopeGame(upload);
    const resp = await fetch(apiUrl('/adminUploadApproval'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${adminToken}`,
      },
      body: JSON.stringify({
        gameId: Number(upload.id),
        approval,
        themeActiveGame: scopeGame,
        gameFilter: scopeGame,
        activeGame: scopeGame,
      }),
    });
    const json = await resp.json().catch(() => ({}));
    if (json.success) {
      await loadUploads();
      Swal.fire('Updated', `Upload ${approval}.`, 'success');
    } else {
      Swal.fire('Error', json.error || 'Could not update approval.', 'error');
    }
  };

  const bulkApprove = async (action) => {
    if (!canBulkAct) {
      Swal.fire(
        'Pick a game',
        'Approve All / Reject All only work when viewing one game, not All games.',
        'warning'
      );
      return;
    }
    const scopeGame = gameFilter;
    const resp = await fetch(apiUrl('/adminUploadApproval'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${adminToken}`,
      },
      body: JSON.stringify({
        action,
        activeGame: scopeGame,
        gameFilter: scopeGame,
        themeActiveGame: scopeGame,
      }),
    });
    const json = await resp.json().catch(() => ({}));
    if (json.success) {
      await loadUploads();
      Swal.fire(
        'Done',
        `${getGameDisplayName(scopeGame)} uploads ${
          action === 'allapproved' ? 'approved' : 'rejected'
        }.`,
        'success'
      );
    } else {
      Swal.fire('Error', json.error || 'Bulk update failed.', 'error');
    }
  };

  const deletePost = async (upload) => {
    const confirm = await Swal.fire({
      title: 'Delete this post?',
      text: 'This permanently removes the post from the feed.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, delete',
    });
    if (!confirm.isConfirmed) return;

    const resp = await fetch(apiUrl('/adminGameDelete'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${adminToken}`,
      },
      body: JSON.stringify({
        gameId: Number(upload.id),
        themeActiveGame: uploadScopeGame(upload),
        gameFilter: uploadScopeGame(upload),
        activeGame: uploadScopeGame(upload),
      }),
    });
    const json = await resp.json().catch(() => ({}));
    if (json.success) {
      await loadUploads();
      Swal.fire('Deleted', 'Post removed.', 'success');
    } else {
      Swal.fire('Error', json.error || 'Could not delete post.', 'error');
    }
  };

  const downloadCsv = () => {
    if (!canBulkAct) {
      Swal.fire(
        'Pick a game',
        'Download CSV is only available when viewing one game.',
        'warning'
      );
      return;
    }
    window.open(
      `${apiUrl('/gameUploadsCsv')}?sessionId=${encodeURIComponent(adminToken.split('&')[0])}&organizationId=${encodeURIComponent(adminToken.split('&')[1])}&activeGame=${encodeURIComponent(gameFilter)}`,
      '_blank'
    );
  };

  const thumbUrl = (u) => {
    const file = u.files?.[0]?.filename || '';
    return resolveGameIconUrl(file) || badgeImageUrl(file);
  };

  const messagePreview = (msg) => {
    const text = String(msg || '');
    return text.length > 200 ? `${text.slice(0, 200)}…` : text;
  };

  return (
    <div className="game-selector-page game-uploads-page">
      <div className="back-button-holder">
        <button type="button" className="back-button" onClick={() => navigate('/admin/rules')}>
          ← Back to rules
        </button>
      </div>
      <h1>Upload Moderation — {filterLabel}</h1>
      <p style={{ marginBottom: '1rem' }}>
        Auto Approve is shared with Rules for the live game ({getGameDisplayName(activeGame)}).
        When Auto Approve is on, new posts appear in the feed immediately. When it is off, posts stay
        pending until you approve them here — one by one or with Approve All.
      </p>

      <div className="game-uploads-toolbar">
        <label className="game-uploads-auto-approve">
          Auto Approve
          <Switch
            checked={autoApprove}
            onChange={persistAutoApprove}
            disabled={savingApproval || !currentTheme}
            onColor="#7c3aed"
            offColor="#cbd5e1"
            height={22}
            width={44}
          />
          <span>{savingApproval ? 'Saving…' : autoApprove ? 'On' : 'Off'}</span>
        </label>
        <label>
          View
          <select value={gameFilter} onChange={(e) => setGameFilter(e.target.value)}>
            <option value={FILTER_ALL}>All games</option>
            {GAME_CATALOG.map((g) => (
              <option key={g.id} value={g.id}>
                {g.name}
              </option>
            ))}
          </select>
        </label>
        <input
          type="search"
          className="game-uploads-search"
          placeholder="Search name, category, message, status, game…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          aria-label="Search uploads"
        />
        {!loading && uploads.length > 0 ? (
          <span className="game-uploads-count">
            {filteredUploads.length} of {uploads.length} post
            {uploads.length === 1 ? '' : 's'}
          </span>
        ) : null}
        <button
          type="button"
          className="gu-btn gu-btn--approve gu-btn--lg"
          disabled={!canBulkAct}
          title={canBulkAct ? undefined : 'Pick one game in View'}
          onClick={() => bulkApprove('allapproved')}
        >
          Approve All ({canBulkAct ? getGameDisplayName(gameFilter) : 'pick a game'})
        </button>
        <button
          type="button"
          className="gu-btn gu-btn--reject gu-btn--lg"
          disabled={!canBulkAct}
          title={canBulkAct ? undefined : 'Pick one game in View'}
          onClick={() => bulkApprove('alldisapproved')}
        >
          Reject All ({canBulkAct ? getGameDisplayName(gameFilter) : 'pick a game'})
        </button>
        <button
          type="button"
          className="gu-btn gu-btn--csv gu-btn--lg"
          disabled={!canBulkAct}
          title={canBulkAct ? undefined : 'Pick one game in View'}
          onClick={downloadCsv}
        >
          Download CSV
        </button>
      </div>

      {loading ? (
        <p>Loading…</p>
      ) : uploads.length === 0 ? (
        <p>No posts yet for this filter.</p>
      ) : filteredUploads.length === 0 ? (
        <p>No posts match your search.</p>
      ) : (
        <table className="game-uploads-table">
          <thead>
            <tr>
              <th>Media</th>
              <th>Name</th>
              <th>Game</th>
              <th>Category</th>
              <th>Message</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredUploads.map((u) => {
              const src = thumbUrl(u);
              const isVideo = (u.files || []).some((f) => String(f.mediaType || '').startsWith('video'))
                || /\.(mp4|mov)$/i.test(u.files?.[0]?.filename || '');
              const status = String(u.approval || 'approved').toLowerCase();
              return (
                <tr key={u.id}>
                  <td>
                    {isVideo ? (
                      '▶ Video'
                    ) : src ? (
                      <img
                        src={src}
                        alt=""
                        style={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 6 }}
                        referrerPolicy="no-referrer"
                      />
                    ) : (
                      '—'
                    )}
                  </td>
                  <td>{u.name}</td>
                  <td>{GAME_TYPE_LABEL[u.gameType] || u.gameType}</td>
                  <td>{u.category}</td>
                  <td>{messagePreview(u.message)}</td>
                  <td>
                    <span className={`game-uploads-status game-uploads-status--${status}`}>
                      {status}
                    </span>
                  </td>
                  <td>
                    <div className="game-uploads-actions">
                      <button
                        type="button"
                        className="gu-btn gu-btn--approve"
                        onClick={() => setApproval(u, 'approved')}
                      >
                        Approve
                      </button>
                      <button
                        type="button"
                        className="gu-btn gu-btn--reject"
                        onClick={() => setApproval(u, 'rejected')}
                      >
                        Reject
                      </button>
                      <button
                        type="button"
                        className="gu-btn gu-btn--delete"
                        onClick={() => deletePost(u)}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}
